import { defineComponent, ref, getCurrentInstance, SetupContext, computed, PropType } from 'vue';
import Toast from 'vant/lib/toast';
import Cookies from 'js-cookie';
// eslint-disable-next-line import/no-extraneous-dependencies
import debounce from 'lodash/debounce';
import { saveAs } from 'file-saver';
import { formateNum, copyToClipboard } from '@/common/utils';
import { Feed, FeedLiked, UserComment, FeedApiService, AuthorFollowing } from '@/api/feed';
import { sendLog } from '@/common/radar';
import { getLlsidFromExptag } from '@/common/common-params';
import { VideoRef } from '../../../type';
import { FeedPageEnum } from '@/store/modules/feed';
import { UserInfoByLogin } from '@/api/user';
import { IconMap, ActionItem, ActionType, ActionBarProps } from './common';
import { useWebToApp, OpenPageStatus } from '~/hooks/useWebToApp';
import { installPwaFun, isContinue } from '~/common/pwa';

export const ActionBarSeoSetup = (props: ActionBarProps, ctx: SetupContext<Array<'onChangeLoginPopupVisible'>>) => {
    const FeedApi = new FeedApiService();
    const { proxy } = getCurrentInstance() || {};
    const isVideoDownloading = ref(false);
    const { checkIfInstalled, installPwa } = useWebToApp();

    const feedList = computed<Feed[]>(() => {
        const { feedListOrigin } = proxy?.$store?.state?.feedMoudle || {};
        return feedListOrigin?.[props?.feedPage || FeedPageEnum.PwaShareNew] || [];
    });

    const handleLikeAction = debounce(async (type: ActionType) => {
        const feedLikeStatus = type === ActionType.Like ? FeedLiked.Like : FeedLiked.UnLike;
        const res = await FeedApi.changeFeedLikedStatus(
            {
                cancel: feedLikeStatus === FeedLiked.UnLike,
                photoId: props?.photoId,
            },
            {
                params: {
                    'kwaipro.pwa_ph': Cookies.get('kwaipro.pwa_ph'),
                },
            }
        );

        if (res?.result !== 1) {
            if (res?.result === 109) {
                // eslint-disable-next-line vue/require-explicit-emits
                ctx.emit('onChangeLoginPopupVisible', true);
                return;
            }

            res?.error_msg &&
                Toast({
                    message: res?.error_msg,
                });
            return;
        }

        const newFeedList = feedList.value?.map?.(feed => {
            if (feed.photo_id_str === props?.photoId) {
                return {
                    ...feed,
                    liked: feedLikeStatus,
                };
            }
            return feed;
        });
        proxy?.$store?.commit?.('feedMoudle/resetFeedList', props.feedPage);
        proxy?.$store?.commit?.('feedMoudle/setFeedList', {
            [props?.feedPage || FeedPageEnum.PwaShareNew]: newFeedList || [],
        });
    }, 300);

    const handleDownloadAction = debounce(async () => {
        try {
            isVideoDownloading.value = true;
            const currentFeed = feedList.value?.find(item => item.photo_id_str === props?.photoId);
            const videoUrl = currentFeed?.main_mv_urls?.find?.(mv => !!mv.url)?.url;
            if (videoUrl) {
                Toast({
                    message: (proxy as Record<string, any>)?.$root?.$t?.('download_started'),
                });

                const response = await fetch(videoUrl);

                if (!response.ok) {
                    return;
                }
                const blob = await response.blob();
                saveAs(blob, `video_${currentFeed?.photo_id_str || ''}.mp4`);
            }
        } catch (e: any) {
            Toast({
                message: e?.message,
            });
        } finally {
            isVideoDownloading.value = false;
        }
    }, 300);

    const handleShareAction = debounce(async (url: string) => {
        const separator = url.includes('?') ? '&' : '?';
        const fix_url = `${url}${separator}pwa_source=web_share`.replace(/@[^/]+(?=\/)/, '@kwai');
        const whatsapp_url = `https://api.whatsapp.com/send?text=${encodeURIComponent(fix_url)}`;
        window.open(whatsapp_url);
    }, 300);

    const likeCount = (count: number) => {
        return formateNum(count || 0).toString();
    };

    const commentCount = (count: number) => {
        return formateNum(count || 0).toString();
    };

    const shareCount = (count: number) => {
        return formateNum(count || 0).toString();
    };

    /**
     *
     * @returns Promise true:桌面逻辑 false:原本逻辑
     */
    const handleActionBarClick = async () => {
        // 检测当前页面状态
        const status = checkIfInstalled();
        // 未安装
        if (status === OpenPageStatus.noPwaInWeb) {
            // installPwaFun 内部有频率检测
            const data = await installPwaFun(installPwa, 'agree_interaction');
            // 安装成功
            if (data?.status === 1) {
                return Promise.resolve(true);
            }

            return Promise.resolve(false);
        }
        if (status === 2) {
            const res = await isContinue('openPwa');
            if (res) {
                // 使用pwa 打开
                (proxy as any).$bus?.emit('openWithPwaDiadog');
                return Promise.resolve(true);
            }
            return Promise.resolve(false);
        }
        return Promise.resolve(false);
    };

    return {
        handleLikeAction,
        handleDownloadAction,
        handleActionBarClick,
        handleShareAction,

        likeCount,
        commentCount,
        shareCount,
    };
};
